/**
 * Ellucian Ethos (Banner SIS) — recurring CUSTOMER SYNCHRONIZATION.
 *
 * A separate section from the credential card above it, because it answers a
 * different question. The credential card asks "can we read Banner at all"
 * (a preflight against resource grants); this section asks "which students
 * become customers here, how often, and what happened last time". They share a
 * connection and nothing else: the credential fields go through
 * `updateIntegrationConfig`, this section through the four `ethos-sync`
 * operations, and a run's outcome invalidates the CUSTOMER caches, not the
 * integration record.
 *
 * Three rules the contract fixes and this file does not restate loosely:
 *
 * 1. **Saving is a full replacement.** `EthosSyncConfigUpdateInput` is `.strict()`
 *    over the whole settings object plus `expected_version` — so every field is
 *    sent on every save, including when automatic sync is off. A partial patch
 *    is not a thing this door accepts.
 * 2. **`enabled` requires a target type**, refined in the schema itself
 *    (`!enabled || customer_type_id !== null`). The Save button enforces the same
 *    rule client-side so the operator learns it at the control, not at the 400.
 * 3. **202 means queued.** `startEthosSyncRun` returns the run, not its result.
 *    Nothing here simulates completion — the row stays `queued`/`running` until a
 *    real read says otherwise, and the mock preserves a queued run on purpose.
 *
 * On the missing-company scope: customer types are `session-company` scoped and
 * the brief asks that changing the active company clear these caches. This app
 * has no company switcher and no company in its session shell — so the state is
 * keyed on the tenant identity, which is the narrowest scope that exists here.
 * When a company selector lands, `scopeKey` is the one line that changes.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Card, Icon, Input, Pill, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import {
  ApiError, getEthosSyncConfig, listEthosSyncRuns, startEthosSyncRun, updateEthosSyncConfig,
} from "@v0y4ge/api-client";
import type { CustomerTypeDto, EthosSyncConfig, EthosSyncRun } from "@v0y4ge/api-contracts";
import { toast } from "../../shell/toast";
import { useT } from "../../lib/i18n";
import { getConfirmedTenantSlug, onTenantChange, onTenantSwitching, useTenant } from "../../shell/access";
import { loadAllCustomerTypes } from "../customers/customer-types";
import { friendlyDateTime } from "../../lib/datetime";

const INTERVAL_MIN = 1;
const INTERVAL_MAX = 10_080;
const RUNS_PAGE = { page: 1, limit: 25 } as const;
const POLL_MS = 5000;

const cardHead: CSSProperties = { padding: "14px 16px", borderBottom: `1px solid ${T.border}` };
const fieldLabel: CSSProperties = { fontSize: 12, fontWeight: 600, color: T.n2, display: "block", marginBottom: 4 };
const helpText: CSSProperties = { fontSize: 11, color: T.soft, lineHeight: 1.5, margin: "4px 0 0" };

/** A run that a later read can still change. Anything else is terminal. */
const isActive = (r: EthosSyncRun): boolean => r.status === "queued" || r.status === "running";

/** Draft shape — exactly the mutable settings, nothing derived. */
export interface EthosSyncDraft {
  enabled: boolean;
  interval_minutes: number;
  customer_type_id: string | null;
  provision_new_customers: boolean;
  /** Held as the raw input string; an empty field maps to `null` on the wire. */
  person_filter_name: string;
}

const draftOf = (c: EthosSyncConfig): EthosSyncDraft => ({
  enabled: c.enabled,
  interval_minutes: c.interval_minutes,
  customer_type_id: c.customer_type_id,
  provision_new_customers: c.provision_new_customers,
  person_filter_name: c.person_filter_name ?? "",
});

const sameDraft = (a: EthosSyncDraft, b: EthosSyncDraft): boolean =>
  a.enabled === b.enabled && a.interval_minutes === b.interval_minutes
  && a.customer_type_id === b.customer_type_id
  && a.provision_new_customers === b.provision_new_customers
  && a.person_filter_name.trim() === b.person_filter_name.trim();

const codeOf = (e: unknown): string => (e instanceof ApiError ? e.code : "");
const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

export interface EthosSyncState {
  /** null + `notConfigured` is a different state from null + `loadError`. */
  config: EthosSyncConfig | null;
  notConfigured: boolean;
  loading: boolean;
  loadError: string | null;
  draft: EthosSyncDraft | null;
  setDraft: (patch: Partial<EthosSyncDraft>) => void;
  dirty: boolean;
  saving: boolean;
  /** Set when a save lost a version race. The draft is kept, never overwritten. */
  conflict: boolean;
  save: () => Promise<void>;
  /** Re-read the server config and adopt it, discarding the draft. */
  reloadConfig: () => Promise<void>;
  discard: () => void;
  types: CustomerTypeDto[];
  activeTypes: CustomerTypeDto[];
  typeName: (id: string | null) => string | null;
  typesError: string | null;
  typesLoading: boolean;
  /** Re-read the whole type catalogue (every page). */
  reloadTypes: () => Promise<void>;
  runs: EthosSyncRun[];
  runsLoading: boolean;
  runsError: string | null;
  starting: boolean;
  startRun: () => Promise<void>;
}

/**
 * One instance, owned by IntegrationDetail alongside `useEthosState`.
 *
 * `active` is false on every other integration's page, and nothing is read then
 * — this hook must not put four calls behind a Salesforce page load.
 */
export function useEthosSync(active: boolean, onRunSettled?: () => void): EthosSyncState {
  const t = useT();
  const tenant = useTenant();
  const scopeKey = tenant?.slug ?? "";
  const generation = useRef(0);
  const requestScope = useRef<string | null>(null);
  const currentRequest = useCallback(() => ({ generation: generation.current, scope: requestScope.current }), []);
  const isCurrent = useCallback((request: { generation: number; scope: string | null }) =>
    request.scope !== null && request.generation === generation.current &&
    request.scope === requestScope.current && request.scope === getConfirmedTenantSlug(), []);

  const [config, setConfig] = useState<EthosSyncConfig | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [draft, setDraftState] = useState<EthosSyncDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [types, setTypes] = useState<CustomerTypeDto[]>([]);
  const [typesError, setTypesError] = useState<string | null>(null);
  const [typesLoading, setTypesLoading] = useState(false);
  const [runs, setRuns] = useState<EthosSyncRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(true);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  /* A manual start is idempotent on its caller-generated id, so a RETRY of the
     same operator action must reuse it. Cleared only once a start succeeds. */
  const pendingRunId = useRef<string | null>(null);
  /* Terminal statuses already reported, so the invalidation fires once per run
     rather than on every poll that still sees the finished row. */
  const settled = useRef<Set<string>>(new Set());

  const readConfig = useCallback(async (adoptDraft: boolean) => {
    const request = currentRequest();
    if (!isCurrent(request)) return;
    try {
      const res = await getEthosSyncConfig();
      if (!isCurrent(request)) return;
      setConfig(res.data);
      setNotConfigured(false);
      setLoadError(null);
      if (adoptDraft) { setDraftState(draftOf(res.data)); setConflict(false); }
    } catch (e) {
      if (!isCurrent(request)) return;
      if (codeOf(e) === "ETHOS_SYNC_NOT_FOUND") {
        setNotConfigured(true);
        setConfig(null);
        setLoadError(null);
      } else {
        setLoadError(messageOf(e));
      }
    } finally { if (isCurrent(request)) setLoading(false); }
  }, [currentRequest, isCurrent]);

  const readRuns = useCallback(async (quiet: boolean) => {
    const request = currentRequest();
    if (!isCurrent(request)) return;
    if (!quiet) setRunsLoading(true);
    try {
      const res = await listEthosSyncRuns(RUNS_PAGE);
      if (!isCurrent(request)) return;
      setRuns(res.data);
      setRunsError(null);
      // Terminal transitions invalidate the customer directory, the
      // type-filtered lists and this configuration — the counts and the
      // `next_run_at` stamp both moved.
      let settledNow = false;
      for (const r of res.data) {
        if (!isActive(r) && !settled.current.has(r.id)) { settled.current.add(r.id); settledNow = true; }
      }
      if (settledNow) { onRunSettled?.(); void readConfig(false); }
    } catch (e) {
      if (!isCurrent(request)) return;
      if (codeOf(e) === "ETHOS_SYNC_NOT_FOUND") { setRuns([]); setRunsError(null); }
      else setRunsError(messageOf(e));
    } finally { if (isCurrent(request) && !quiet) setRunsLoading(false); }
  }, [onRunSettled, readConfig, currentRequest, isCurrent]);

  /* Every page of the catalogue, not just the first 200: a selected type on
     page two must resolve to its name and stay selectable. An incomplete read
     (empty page before the advertised total) is an ERROR with a retry, never a
     silently truncated list. Archived rows are kept for label resolution; the
     select offers only active ones. */
  const readTypes = useCallback(async () => {
    const request = currentRequest();
    if (!isCurrent(request)) return;
    setTypesLoading(true);
    try {
      const all = await loadAllCustomerTypes();
      if (isCurrent(request)) { setTypes(all); setTypesError(null); }
    } catch (e) {
      if (isCurrent(request)) setTypesError(messageOf(e));
    } finally { if (isCurrent(request)) setTypesLoading(false); }
  }, [currentRequest, isCurrent]);

  /* Purge on optimistic switching; read only once the new session is confirmed. */
  useEffect(() => {
    const clear = () => {
      generation.current += 1;
      requestScope.current = null;
      pendingRunId.current = null;
      settled.current = new Set();
      setLoading(true); setRunsLoading(true); setSaving(false); setStarting(false);
      setConfig(null); setDraftState(null); setRuns([]); setTypes([]); setTypesLoading(false);
      setConflict(false); setNotConfigured(false); setLoadError(null); setRunsError(null); setTypesError(null);
    };
    const load = async () => {
      clear();
      if (!active) return;
      requestScope.current = getConfirmedTenantSlug();
      const request = currentRequest();
      if (!isCurrent(request)) return;
      await readTypes();
      if (isCurrent(request)) await readConfig(true);
      if (isCurrent(request)) await readRuns(false);
    };
    const offSwitching = onTenantSwitching(clear);
    const offChange = onTenantChange(() => { void load(); });
    void load();
    return () => {
      generation.current += 1;
      requestScope.current = null;
      offSwitching(); offChange();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, scopeKey]);

  /* Poll only while a run can still change AND the tab is visible. Stops on
     unmount and on hide; resumes with a fresh read when the tab comes back. */
  const anyActive = runs.some(isActive);
  useEffect(() => {
    if (!active || !anyActive) return;
    let timer: number | null = null;
    const stop = () => { if (timer != null) { window.clearInterval(timer); timer = null; } };
    const start = () => { stop(); timer = window.setInterval(() => { void readRuns(true); }, POLL_MS); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") { void readRuns(true); start(); } else stop();
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => { stop(); document.removeEventListener("visibilitychange", onVisibility); };
  }, [active, anyActive, readRuns]);

  const setDraft = useCallback((patch: Partial<EthosSyncDraft>) => {
    setDraftState((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const save = useCallback(async () => {
    const request = currentRequest();
    if (!isCurrent(request) || !config || !draft || saving) return;
    setSaving(true);
    try {
      const filter = draft.person_filter_name.trim();
      const res = await updateEthosSyncConfig({
        expected_version: config.version,
        enabled: draft.enabled,
        interval_minutes: draft.interval_minutes,
        customer_type_id: draft.customer_type_id,
        provision_new_customers: draft.provision_new_customers,
        person_filter_name: filter.length ? filter : null,
      });
      if (!isCurrent(request)) return;
      setConfig(res.data);
      setDraftState(draftOf(res.data));
      setConflict(false);
      toast({
        tone: "success",
        title: t("settings.ethosSync.savedTitle", "Customer sync saved"),
        subtitle: res.data.enabled
          ? t("settings.ethosSync.savedOnSub", "The schedule is active. Changing the interval needs no deployment.")
          : t("settings.ethosSync.savedOffSub", "Automatic sync is off \u2014 \u201cSync now\u201d still runs on demand."),
      });
    } catch (e) {
      if (!isCurrent(request)) return;
      const code = codeOf(e);
      if (code === "ETHOS_SYNC_CONFLICT") {
        // Someone else saved first. The draft is deliberately NOT replaced —
        // offering a reload is the only honest move; overwriting a concurrent
        // change silently is the failure this version check exists to prevent.
        setConflict(true);
      } else if (code === "ETHOS_SYNC_NOT_FOUND") {
        setNotConfigured(true);
        toast({ tone: "warn", title: t("settings.ethosSync.noConnectionTitle", "No Ethos connection"), subtitle: t("settings.ethosSync.noConnectionSub", "Configure the connection credentials above first.") });
      } else {
        toast({ tone: "warn", title: t("settings.ethosSync.saveFailedTitle", "Couldn\u2019t save the sync settings"), subtitle: messageOf(e) });
      }
    } finally { if (isCurrent(request)) setSaving(false); }
  }, [config, draft, saving, t, currentRequest, isCurrent]);

  const startRun = useCallback(async () => {
    const request = currentRequest();
    if (!isCurrent(request) || starting) return;
    setStarting(true);
    const id = pendingRunId.current ?? crypto.randomUUID();
    pendingRunId.current = id;
    try {
      const res = await startEthosSyncRun({ id });
      if (!isCurrent(request)) return;
      pendingRunId.current = null;
      // 202: queued, not complete. Show the returned run immediately, then let
      // the poll carry it — no optimistic success wording.
      setRuns((prev) => [res.data, ...prev.filter((r) => r.id !== res.data.id)]);
      toast({ tone: "success", title: t("settings.ethosSync.queuedTitle", "Sync queued"), subtitle: t("settings.ethosSync.queuedSub", "Progress appears in the run history below.") });
      await readRuns(true);
    } catch (e) {
      if (!isCurrent(request)) return;
      const code = codeOf(e);
      if (code === "ETHOS_SYNC_CONFLICT") {
        // A run is already active. Retrying is pointless — show the one that
        // exists instead of a second queue entry.
        await readRuns(true);
        if (!isCurrent(request)) return;
        toast({ tone: "warn", title: t("settings.ethosSync.alreadyRunningTitle", "A sync is already running"), subtitle: t("settings.ethosSync.alreadyRunningSub", "The run in progress is shown below.") });
      } else if (code === "ETHOS_SYNC_NOT_FOUND") {
        setNotConfigured(true);
        toast({ tone: "warn", title: t("settings.ethosSync.noConnectionTitle", "No Ethos connection"), subtitle: t("settings.ethosSync.noConnectionSub", "Configure the connection credentials above first.") });
      } else {
        toast({ tone: "warn", title: t("settings.ethosSync.startFailedTitle", "Couldn\u2019t start the sync"), subtitle: messageOf(e) });
      }
    } finally { if (isCurrent(request)) setStarting(false); }
  }, [starting, readRuns, t, currentRequest, isCurrent]);

  const activeTypes = useMemo(() => types.filter((x) => x.status === "active"), [types]);
  const typeName = useCallback(
    (id: string | null) => (id ? types.find((x) => x.id === id)?.name ?? null : null),
    [types],
  );

  return {
    config, notConfigured, loading, loadError,
    draft, setDraft,
    dirty: config != null && draft != null && !sameDraft(draft, draftOf(config)),
    saving, conflict, save,
    reloadConfig: () => readConfig(true),
    discard: () => { if (config) { setDraftState(draftOf(config)); setConflict(false); } },
    types, activeTypes, typeName, typesError, typesLoading, reloadTypes: readTypes,
    runs, runsLoading, runsError, starting, startRun,
  };
}

/* ── Presentation ─────────────────────────────────────────────────────────── */

function Field({ label, help, error, children }: { label: string; help?: string; error?: string | null; children: ReactNode }) {
  return (
    <div>
      <label style={fieldLabel}>{label}</label>
      {children}
      {error ? <p style={{ ...helpText, color: T.red1 }}>{error}</p> : help ? <p style={helpText}>{help}</p> : null}
    </div>
  );
}

/** A stamp whose null means "no run yet" — never a blank cell, never a fake date. */
function Stamp({ label, at, none }: { label: string; at: string | null; none: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 13, color: at ? T.n1 : T.soft, marginTop: 2 }}>{at ? friendlyDateTime(at) : none}</div>
    </div>
  );
}

export function EthosSyncCard({ state, canWrite }: { state: EthosSyncState; canWrite: boolean }) {
  const t = useT();
  const { config, draft } = state;
  const [intervalText, setIntervalText] = useState<string>("");
  const lastSeenInterval = useRef<number | null>(null);

  // The interval input is a text field over an integer, so a half-typed value
  // ("1", on the way to "15") must not be coerced or rejected mid-keystroke.
  useEffect(() => {
    if (draft && draft.interval_minutes !== lastSeenInterval.current) {
      lastSeenInterval.current = draft.interval_minutes;
      setIntervalText(String(draft.interval_minutes));
    }
  }, [draft]);

  if (state.loading) {
    return <Card style={{ padding: 24, fontSize: 12, color: T.soft }}>{t("settings.ethosSync.loading", "Loading the customer sync configuration\u2026")}</Card>;
  }

  if (state.notConfigured) {
    return (
      <Card style={{ padding: 0 }}>
        <div style={cardHead}>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.ethosSync.title", "Customer synchronization")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.ethosSync.help", "Brings SIS students into this company\u2019s customer records on a schedule.")}</p>
        </div>
        <div style={{ padding: 16, display: "flex", gap: 10, alignItems: "flex-start", background: T.n6 }}>
          <span style={{ display: "inline-flex", marginTop: 1, color: T.soft }}><Icon.info size={14} /></span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.ethosSync.notConfiguredTitle", "No Ethos connection is configured")}</div>
            <div style={{ fontSize: 12, color: T.n2, marginTop: 2, lineHeight: 1.6 }}>{t("settings.ethosSync.notConfiguredBody", "Enter the base URL and API key above and verify the resource grants. The sync schedule attaches to that connection \u2014 there is nothing to configure until it exists.")}</div>
          </div>
        </div>
      </Card>
    );
  }

  if (state.loadError || !config || !draft) {
    return (
      <Card style={{ padding: 16, background: `color-mix(in srgb, ${T.red1} 7%, ${T.white})`, borderColor: `color-mix(in srgb, ${T.red1} 25%, transparent)` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.red1 }}>{t("settings.ethosSync.loadFailedTitle", "Customer sync settings are unavailable")}</div>
        <div style={{ fontSize: 12, color: T.n1, marginTop: 4 }}>{state.loadError || t("settings.ethosSync.loadFailedSub", "The configuration didn\u2019t load, so no form is shown rather than an empty one that would look saveable.")}</div>
      </Card>
    );
  }

  const parsedInterval = /^\d+$/.test(intervalText.trim()) ? Number(intervalText.trim()) : NaN;
  const intervalError = Number.isNaN(parsedInterval)
    ? t("settings.ethosSync.intervalNotNumber", "Enter the interval in whole minutes.")
    : parsedInterval < INTERVAL_MIN || parsedInterval > INTERVAL_MAX
      ? t("settings.ethosSync.intervalRange", "Between 1 minute and 10080 (7 days).")
      : null;
  // The schema's own refinement, enforced at the control: an enabled schedule
  // with no target type has nowhere to put the students it reads.
  const needsType = draft.enabled && draft.customer_type_id == null;
  const blocked = intervalError != null || needsType;
  const canSave = canWrite && state.dirty && !blocked && !state.saving;
  const manualBlocked = draft.customer_type_id == null || state.dirty;

  const selStyle: CSSProperties = {
    width: "100%", height: 36, padding: "0 10px", borderRadius: 6,
    border: `1px solid ${needsType ? T.red1 : T.borderStrong}`, background: canWrite ? T.white : T.n6,
    fontFamily: T.font, fontSize: 13, color: draft.customer_type_id ? T.n1 : T.soft,
  };

  return (
    <Card style={{ padding: 0 }}>
      <div style={{ ...cardHead, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.ethosSync.title", "Customer synchronization")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.ethosSync.help", "Brings SIS students into this company\u2019s customer records on a schedule.")}</p>
        </div>
        {config.enabled
          ? <Pill tone="success" dot>{t("settings.ethosSync.pillOn", "Scheduled")}</Pill>
          : <Pill tone="neutral">{t("settings.ethosSync.pillOff", "Manual only")}</Pill>}
      </div>

      {state.conflict ? (
        <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, background: T.orange2, display: "flex", gap: 10, alignItems: "flex-start" }}>
          <span style={{ display: "inline-flex", marginTop: 1, color: T.orange1 }}><Icon.warn size={14} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{t("settings.ethosSync.conflictTitle", "Someone else saved these settings first")}</div>
            <div style={{ fontSize: 12, color: T.n2, marginTop: 2, lineHeight: 1.6 }}>{t("settings.ethosSync.conflictBody", "Your edits are still here and nothing was overwritten. Load the current configuration to see what changed, then reapply what you need.")}</div>
          </div>
          <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} onClick={() => { void state.reloadConfig(); }}>
            {t("settings.ethosSync.conflictReload", "Load current")}
          </Btn>
        </div>
      ) : null}

      <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: canWrite ? "pointer" : "default" }}>
          <span style={{ marginTop: 1, display: "inline-flex" }}>
            <Toggle on={draft.enabled} onChange={canWrite ? () => state.setDraft({ enabled: !draft.enabled }) : undefined} />
          </span>
          <span>
            <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: T.n1 }}>{t("settings.ethosSync.enabledLabel", "Sync customers automatically")}</span>
            <span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 2, lineHeight: 1.5 }}>{t("settings.ethosSync.enabledHelp", "Off leaves the connection in place; \u201cSync now\u201d still works.")}</span>
          </span>
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
          <Field
            label={t("settings.ethosSync.intervalLabel", "Interval (minutes)")}
            help={t("settings.ethosSync.intervalHelp", "How often a scheduled run starts \u2014 not a promise it finishes within that window.")}
            error={intervalError}
          >
            <Input
              value={intervalText}
              inputMode="numeric"
              readOnly={!canWrite}
              onChange={canWrite ? (e: React.ChangeEvent<HTMLInputElement>) => {
                const next = e.target.value;
                setIntervalText(next);
                if (/^\d+$/.test(next.trim())) {
                  const n = Number(next.trim());
                  lastSeenInterval.current = n;
                  state.setDraft({ interval_minutes: n });
                }
              } : undefined}
            />
          </Field>

          <Field
            label={t("settings.ethosSync.typeLabel", "Target customer type")}
            help={needsType
              ? undefined
              : t("settings.ethosSync.typeHelp", "Synced students are classified as this type. Only active types can be chosen.")}
            error={needsType ? t("settings.ethosSync.typeRequired", "An enabled sync needs a target type.") : null}
          >
            {/* ui-raw: a plain listbox of company types. FieldSelect owns its own
                label/help chrome, which would double up inside Field. */}
            <select
              value={draft.customer_type_id ?? ""}
              disabled={!canWrite}
              style={selStyle}
              onChange={(e) => state.setDraft({ customer_type_id: e.target.value ? e.target.value : null })}
            >
              <option value="">{t("settings.ethosSync.typeNone", "None selected")}</option>
              {state.activeTypes.map((ct) => <option key={ct.id} value={ct.id}>{ct.name}</option>)}
              {/* An assignment can legitimately point at an archived type. It
                  stays selectable-as-current so saving an unrelated field does
                  not silently clear it — but it is labelled for what it is. */}
              {draft.customer_type_id && !state.activeTypes.some((ct) => ct.id === draft.customer_type_id) ? (
                <option value={draft.customer_type_id}>
                  {t("settings.ethosSync.typeArchived", "{type} — archived", { type: state.typeName(draft.customer_type_id) ?? t("settings.ethosSync.typeUnknown", "Unknown type") })}
                </option>
              ) : null}
            </select>
          </Field>
        </div>

        {state.typesError ? (
          <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12, color: T.n1, padding: "8px 10px", borderRadius: 6, background: T.orange2 }}>
            <span style={{ display: "inline-flex", color: T.orange1 }}><Icon.warn size={13} /></span>
            <span style={{ flex: 1 }}>{t("settings.ethosSync.typesUnavailable", "The customer type list couldn\u2019t be read completely, so the choices above may be incomplete.")} <span style={{ color: T.soft }}>{state.typesError}</span></span>
            <Btn variant="secondary" size="sm" icon={<Icon.refresh size={12} />} disabled={state.typesLoading} onClick={() => { void state.reloadTypes(); }}>
              {state.typesLoading ? t("settings.ethosSync.typesRetrying", "Reading\u2026") : t("common.actions.retry", "Retry")}
            </Btn>
          </div>
        ) : state.typesLoading ? (
          <div style={{ fontSize: 12, color: T.soft }}>{t("settings.ethosSync.typesLoading", "Reading customer types\u2026")}</div>
        ) : null}

        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: canWrite ? "pointer" : "default" }}>
          <span style={{ marginTop: 1, display: "inline-flex" }}>
            <Toggle on={draft.provision_new_customers} onChange={canWrite ? () => state.setDraft({ provision_new_customers: !draft.provision_new_customers }) : undefined} />
          </span>
          <span>
            <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: T.n1 }}>{t("settings.ethosSync.provisionLabel", "Create customers for new SIS students")}</span>
            <span style={{ display: "block", fontSize: 12, color: T.soft, marginTop: 2, lineHeight: 1.5 }}>{t("settings.ethosSync.provisionHelp", "Off means a run only updates students who already have a customer record here.")}</span>
          </span>
        </label>

        <Field
          label={t("settings.ethosSync.filterLabel", "Person filter (optional)")}
          help={t("settings.ethosSync.filterHelp", "The name of a saved person filter in Ethos. Leave empty to sync every student the grants expose.")}
        >
          <Input
            value={draft.person_filter_name}
            placeholder={t("settings.ethosSync.filterPlaceholder", "e.g. Active undergraduates")}
            readOnly={!canWrite}
            onChange={canWrite ? (e: React.ChangeEvent<HTMLInputElement>) => state.setDraft({ person_filter_name: e.target.value }) : undefined}
          />
        </Field>

        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", paddingTop: 4, borderTop: `1px solid ${T.border}` }}>
          <Stamp label={t("settings.ethosSync.nextRun", "Next scheduled run")} at={config.enabled ? config.next_run_at : null}
            none={config.enabled ? t("settings.ethosSync.nextRunNone", "Not scheduled yet") : t("settings.ethosSync.nextRunOff", "Automatic sync is off")} />
          <Stamp label={t("settings.ethosSync.lastSuccess", "Last successful run")} at={config.last_success_at}
            none={t("settings.ethosSync.lastSuccessNone", "No run yet")} />
        </div>
      </div>

      {canWrite ? (
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${T.border}`, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <Btn disabled={!canSave} onClick={() => { void state.save(); }}>
            {state.saving ? t("common.actions.saving", "Saving\u2026") : t("common.actions.save", "Save")}
          </Btn>
          {state.dirty ? (
            <Btn variant="ghost" disabled={state.saving} onClick={state.discard}>{t("settings.ethosSync.discard", "Discard changes")}</Btn>
          ) : null}
          <span style={{ flex: 1 }} />
          {/* Manual sync needs a configured type but NOT an enabled schedule. */}
          <Btn variant="secondary" icon={<Icon.refresh size={14} />} disabled={state.starting || manualBlocked}
            onClick={() => { void state.startRun(); }}>
            {state.starting ? t("settings.ethosSync.starting", "Starting\u2026") : t("settings.ethosSync.syncNow", "Sync now")}
          </Btn>
          {manualBlocked ? (
            <span style={{ fontSize: 11, color: T.soft, width: "100%" }}>
              {draft.customer_type_id == null
                ? t("settings.ethosSync.manualNeedsType", "Choose and save a target customer type before running a sync.")
                : t("settings.ethosSync.manualNeedsSave", "Save the pending changes first \u2014 a run uses the stored configuration, not the draft.")}
            </span>
          ) : null}
        </div>
      ) : (
        <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, fontSize: 11, color: T.soft }}>
          {t("settings.ethosSync.readOnly", "You have read access to these settings. Changing them needs the integrations write permission.")}
        </div>
      )}
    </Card>
  );
}

const STATUS_TONE: Record<EthosSyncRun["status"], "neutral" | "success" | "warn" | "danger"> = {
  queued: "neutral", running: "neutral", succeeded: "success", partial: "warn", failed: "danger",
};

export function EthosSyncHistoryCard({ state }: { state: EthosSyncState }) {
  const t = useT();
  const label: Record<EthosSyncRun["status"], string> = {
    queued: t("settings.ethosSync.statusQueued", "Queued"),
    running: t("settings.ethosSync.statusRunning", "Running"),
    succeeded: t("settings.ethosSync.statusSucceeded", "Succeeded"),
    partial: t("settings.ethosSync.statusPartial", "Partial"),
    failed: t("settings.ethosSync.statusFailed", "Failed"),
  };
  if (state.notConfigured) return null;
  return (
    <Card style={{ padding: 0 }}>
      <div style={{ ...cardHead, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.ethosSync.historyTitle", "Sync runs")}</h3>
          <p style={{ ...tx.help, marginTop: 2 }}>{t("settings.ethosSync.historyHelp", "The 25 most recent runs. A queued or running row refreshes every few seconds while this tab is open.")}</p>
        </div>
        {state.runs.some(isActive) ? <Pill tone="neutral" dot>{t("settings.ethosSync.live", "Refreshing")}</Pill> : null}
      </div>
      {state.runsLoading ? (
        <div style={{ padding: 16, fontSize: 12, color: T.soft }}>{t("settings.ethosSync.historyLoading", "Loading run history\u2026")}</div>
      ) : state.runsError ? (
        <div style={{ padding: 16, fontSize: 12, color: T.orange1 }}>{state.runsError}</div>
      ) : state.runs.length === 0 ? (
        <div style={{ padding: 16, fontSize: 12, color: T.soft }}>{t("settings.ethosSync.historyEmpty", "No sync has run yet. A scheduled or manual run appears here as soon as it is queued.")}</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {state.runs.map((r, i) => (
            <li key={r.id} style={{ padding: "12px 16px", borderTop: i ? `1px solid ${T.border}` : "none", display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <Pill tone={STATUS_TONE[r.status]} dot={isActive(r)}>{label[r.status]}</Pill>
                <span style={{ fontSize: 12, color: T.n2 }}>
                  {r.trigger === "manual" ? t("settings.ethosSync.triggerManual", "Manual") : t("settings.ethosSync.triggerScheduled", "Scheduled")}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: T.soft }}>
                  {r.completed_at ? friendlyDateTime(r.completed_at)
                    : r.started_at ? t("settings.ethosSyncSection.text2", "{arg1} {arg2}", { arg1: t("settings.ethosSync.startedAt", "Started"), arg2: friendlyDateTime(r.started_at) })
                    : t("settings.ethosSyncSection.text2", "{arg1} {arg2}", { arg1: t("settings.ethosSync.queuedAt", "Queued"), arg2: friendlyDateTime(r.created_at) })}
                </span>
              </div>
              {/* Counts render for every status, including a failed run: "0
                  created, 4 errors" is the operational fact, and hiding it would
                  make a partial run indistinguishable from a total failure. */}
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: T.n1 }}>
                <span>{t("settings.ethosSyncSection.text3", "{created_count} {arg2}", { created_count: r.created_count, arg2: t("settings.ethosSync.created", "created") })}</span>
                <span>{t("settings.ethosSyncSection.text4", "{updated_count} {arg2}", { updated_count: r.updated_count, arg2: t("settings.ethosSync.updated", "updated") })}</span>
                <span>{t("settings.ethosSyncSection.text5", "{unchanged_count} {arg2}", { unchanged_count: r.unchanged_count, arg2: t("settings.ethosSync.unchanged", "unchanged") })}</span>
                <span style={{ color: r.error_count ? T.red1 : T.soft }}>{t("settings.ethosSyncSection.text6", "{error_count} {arg2}", { error_count: r.error_count, arg2: t("settings.ethosSync.errors", "errors") })}</span>
              </div>
              {r.last_error ? (
                <div style={{ fontSize: 12, color: T.n2, lineHeight: 1.5, background: T.n6, borderRadius: 6, padding: "8px 10px" }}>{r.last_error}</div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
